import React, { useState, useEffect } from 'react';
import { BookOpen, MessageSquare, ChevronDown, ChevronUp, Star } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { lessonRatingsService } from '../../../services/lessonRatingsService';

const LessonCard = ({ lesson }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { user } = useAuth();

  const [userRating, setUserRating] = useState(0);
  const [avgRating, setAvgRating] = useState(null);
  const [ratingCount, setRatingCount] = useState(0);

  const fetchRatings = async () => {
    try {
      const id = lesson?.id;
      if (!id) return;
      const rows = await lessonRatingsService.getLessonRatings(id);
      if (!rows || rows.length === 0) {
        setAvgRating(null);
        setRatingCount(0);
        setUserRating(0);
        return;
      }
      const sum = rows.reduce((s, r) => s + Number(r.rating || 0), 0);
      const avg = sum / rows.length;
      setAvgRating(avg);
      setRatingCount(rows.length);
      const mine = rows.find(r => r.user_id === user?.id);
      setUserRating(mine ? Number(mine.rating) : 0);
    } catch (err) {
      console.error('Error fetching ratings', err);
    }
  };

  useEffect(() => {
    if (isExpanded) fetchRatings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpanded]);

  const avgDisplay = () => {
    if (!avgRating) return ratingCount > 0 ? `${Number(avgRating).toFixed(1)} (${ratingCount})` : 'No ratings';
    return `${Number(avgRating).toFixed(1)} (${ratingCount})`;
  };

  const getDifficultyColor = (difficulty) => {
    const colors = {
      1: 'bg-green-100 text-green-800',
      2: 'bg-blue-100 text-blue-800',
      3: 'bg-yellow-100 text-yellow-800',
      4: 'bg-orange-100 text-orange-800',
      5: 'bg-red-100 text-red-800'
    };
    return colors?.[difficulty] || 'bg-gray-100 text-gray-800';
  };

  const getDifficultyLabel = (difficulty) => {
    const labels = {
      1: 'Beginner',
      2: 'Intermediate',
      3: 'Advanced',
      4: 'Expert',
      5: 'Master'
    };
    return labels?.[difficulty] || 'Unknown';
  };

  // Stars/ratings removed per UX decision; keep difficulty label instead

  const parseKeySteps = (keySteps) => {
    if (!keySteps) return [];
    return keySteps?.split('\n')?.filter(step => step?.trim());
  };

  // Tags removed from tiles; tags may be used later for filtering/search

  return (
    <div className="bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300 overflow-hidden">
      {/* Card Header */}
      <div className="p-6 border-b border-gray-100">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-blue-600" />
            <span className="text-sm font-medium text-gray-500 capitalize">
              {lesson?.industry}
            </span>
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getDifficultyColor(lesson?.difficulty)}`}>
            {getDifficultyLabel(lesson?.difficulty)}
          </span>
        </div>

        <h3 className="text-xl font-bold text-gray-800 mb-2">{lesson?.title}</h3>

        {/* Difficulty indicator (label only) */}
        <div className="mb-4">
          <span className="text-sm text-gray-500">{getDifficultyLabel(lesson?.difficulty)}</span>
        </div>

        {/* Lesson Text */}
        <p className="text-gray-600 text-sm leading-relaxed mb-4">
          {lesson?.lessonText}
        </p>

        {/* Tags removed from lesson tiles */}

        {/* Expand/Collapse Button */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium text-sm transition-colors"
        >
          {isExpanded ? (
            <>
              <ChevronUp className="w-4 h-4" />
              Show Less
            </>
          ) : (
            <>
              <ChevronDown className="w-4 h-4" />
              View Details
            </>
          )}
        </button>
      </div>
      {/* Expanded Content */}
      {isExpanded && (
        <div className="p-6 bg-gray-50 space-y-6">
          {/* Ratings UI */}
          <div>
            <h4 className="text-lg font-semibold text-gray-800 mb-3">Your Rating</h4>
            <div className="flex items-center gap-2">
              {Array.from({ length: 5 }).map((_, i) => {
                const val = i + 1;
                const filled = userRating >= val;
                return (
                  <button
                    key={val}
                    onClick={async () => {
                      try {
                        const { id } = lesson || {};
                        if (!id || !user?.id) return;
                        await lessonRatingsService.addOrUpdateRating({ userId: user.id, lessonId: id, rating: val });
                        setUserRating(val);
                        // refresh avg
                        fetchRatings();
                      } catch (err) {
                        console.error('Error saving rating', err);
                      }
                    }}
                    className={`p-1 ${filled ? 'text-yellow-500' : 'text-gray-300'}`}
                    aria-label={`Rate ${val} star`}
                  >
                    <Star className={`w-5 h-5 ${filled ? 'fill-current' : ''}`} />
                  </button>
                );
              })}
              <span className="text-sm text-gray-600">{avgDisplay()}</span>
            </div>
          </div>
          {/* Key Steps */}
          <div>
            <h4 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-blue-600" />
              Key Steps
            </h4>
            <ul className="space-y-2">
              {parseKeySteps(lesson?.keySteps)?.map((step, index) => (
                <li key={index} className="text-gray-700 text-sm flex items-start gap-2">
                  <span className="inline-flex items-center justify-center w-6 h-6 bg-blue-100 text-blue-600 rounded-full text-xs font-bold flex-shrink-0">
                    {index + 1}
                  </span>
                  <span className="pt-0.5">{step?.replace(/^\d+\.\s*/, '')}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Example Dialogue */}
          {lesson?.exampleDialogue && (
            <div>
              <h4 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-green-600" />
                Example Dialogue
              </h4>
              <div className="bg-white rounded-lg p-4 border-l-4 border-green-500">
                <p className="text-gray-700 text-sm leading-relaxed italic">
                  {lesson?.exampleDialogue}
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LessonCard;